import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Building2, LogOut, Users, ShoppingCart, IndianRupee, Power, Search } from "lucide-react";
import { Loading, EmptyState, Money } from "@/components/common";
import { formatDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

export default function PlatformDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [businesses, setBusinesses] = useState(null);
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    try {
      const [s, b] = await Promise.all([api.get("/platform/stats"), api.get("/platform/businesses")]);
      setStats(s.data); setBusinesses(b.data);
    } catch (e) { setBusinesses(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (biz, active) => {
    try {
      await api.patch(`/platform/businesses/${biz.id}`, { active });
      toast.success(`${biz.name} ${active ? "activated" : "suspended"}`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const doLogout = async () => { await logout(); navigate("/login"); };
  const filtered = (businesses || []).filter((b) =>
    b.name.toLowerCase().includes(q.toLowerCase()) || (b.owner_email || "").toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="h-16 bg-[#0f172a] sticky top-0 z-20">
        <div className="max-w-6xl mx-auto h-full px-4 sm:px-6 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="h-8 w-8 rounded-lg bg-blue-600 grid place-items-center text-white font-bold font-display">V</div>
            <div>
              <div className="text-white font-bold font-display leading-none">VYOMRA Platform</div>
              <div className="text-[10px] text-slate-400 mt-0.5">Super Admin Console</div>
            </div>
          </div>
          <button onClick={doLogout} data-testid="platform-logout" className="inline-flex items-center gap-1.5 text-sm text-slate-300 hover:text-white">
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-display text-slate-900">All Businesses</h1>
          <p className="text-sm text-slate-500 mt-1">Manage every business using VYOMRA CRM.</p>
        </div>

        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <Stat icon={Building2} label="Businesses" value={stats.total_businesses} sub={`${stats.active_businesses} active · ${stats.suspended_businesses} suspended`} />
            <Stat icon={ShoppingCart} label="Total Orders" value={stats.total_orders} />
            <Stat icon={Users} label="Total Users" value={stats.total_users} />
            <Stat icon={IndianRupee} label="Total Collected" value={<Money value={stats.total_revenue} />} />
          </div>
        )}

        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input data-testid="platform-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search business or owner" className="pl-9 bg-white" />
        </div>

        {businesses === null ? <Loading /> : filtered.length === 0 ? (
          <div className="bg-white border rounded-xl shadow-sm"><EmptyState icon={Building2} title="No businesses yet" sub="Businesses appear here when they sign up." /></div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((b) => (
              <div key={b.id} data-testid={`business-card-${b.id}`} className="bg-white border rounded-xl shadow-sm p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-bold font-display text-slate-900 truncate">{b.name}</div>
                    <div className="text-xs text-slate-500 truncate">{b.owner_email || "—"}</div>
                  </div>
                  <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium shrink-0 ${b.active ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}>
                    {b.active ? "Active" : "Suspended"}
                  </span>
                </div>
                <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                  <Metric label="Orders" value={b.order_count} />
                  <Metric label="Users" value={b.user_count} />
                  <Metric label="Revenue" value={<Money value={b.revenue} />} small />
                </div>
                <div className="mt-4 flex items-center justify-between border-t pt-3">
                  <span className="text-xs text-slate-400">Since {formatDate(b.created_at)}</span>
                  <div className="flex items-center gap-2">
                    <Power className={`h-3.5 w-3.5 ${b.active ? "text-emerald-600" : "text-slate-400"}`} />
                    <Switch checked={b.active} onCheckedChange={(v) => toggle(b, v)} data-testid={`toggle-active-${b.id}`} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, sub }) {
  return (
    <div className="bg-white border rounded-xl shadow-sm p-4">
      <div className="flex items-center gap-2 text-slate-500"><Icon className="h-4 w-4" /><span className="text-xs font-medium">{label}</span></div>
      <div className="mt-2 text-2xl font-bold font-display text-slate-900">{value}</div>
      {sub && <div className="text-[11px] text-slate-400 mt-1">{sub}</div>}
    </div>
  );
}

function Metric({ label, value, small }) {
  return (
    <div className="rounded-lg bg-slate-50 py-2">
      <div className={`font-bold font-display text-slate-900 ${small ? "text-sm" : "text-base"}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-wide text-slate-400 mt-0.5">{label}</div>
    </div>
  );
}
